import Modal from "@/components/atoms/Modal";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { IModalMethods, IModalProps } from "@/interfaces/Modal";
import {
  useGetCompanyStatusPermissionsQuery,
  useUpdateCompanyStatusPermissionsMutation,
} from "@/services/companies/statusPermissions";
import { UserRoleType } from "@/interfaces/IUser";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Form, Spinner, Table } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface ManageCompanyStatusPermissionsModalProps
  extends Omit<IModalProps, "children" | "onPress"> {
  onHide?: () => void;
}

interface PermissionFormData {
  permissions: {
    [key in UserRoleType]?: {
      minOrder: number;
      maxOrder: number | null;
    };
  };
}

const ManageCompanyStatusPermissionsModal: ForwardRefRenderFunction<
  IModalMethods,
  ManageCompanyStatusPermissionsModalProps
> = ({ onHide, ...props }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const { data: permissions, isLoading } =
    useGetCompanyStatusPermissionsQuery();
  const [updatePermissionsAsync] = useUpdateCompanyStatusPermissionsMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PermissionFormData>();

  useEffect(() => {
    if (permissions) {
      const defaultValues: PermissionFormData = {
        permissions: {},
      };
      permissions.forEach((permission) => {
        defaultValues.permissions[permission.role] = {
          minOrder: permission.minOrder,
          maxOrder: permission.maxOrder,
        };
      });
      reset(defaultValues);
    }
  }, [permissions, reset]);

  const onSubmit: SubmitHandler<PermissionFormData> = async (data) => {
    try {
      await toast.promise(
        updatePermissionsAsync({ permissions: data.permissions }).unwrap(),
        {
          pending: "Mise à jour des permissions en cours...",
          success: {
            render() {
              showOrHiddenModal();
              return "Les permissions ont bien été mises à jour 🤩";
            },
          },
          error: {
            render({}: ToastContentProps<FetchBaseQueryError>) {
              return "Une erreur est survenue";
            },
          },
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  const onError: SubmitErrorHandler<PermissionFormData> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      {...props}
      onSubmit={handleSubmit(onSubmit, onError)}
      onHide={onHide}
    >
      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <p className="text-muted mb-3">
            Définissez les plages d'ordres que chaque rôle peut modifier.
            Laissez "Ordre max" vide pour un accès illimité.
          </p>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Rôle</th>
                <th>Ordre min</th>
                <th>Ordre max</th>
              </tr>
            </thead>
            <tbody>
              {permissions?.map((permission) => (
                <tr key={permission.id}>
                  <td className="align-middle">
                    <strong>{permission.role}</strong>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="1"
                      {...register(`permissions.${permission.role}.minOrder`, {
                        required: "Ce champ est obligatoire",
                        min: {
                          value: 1,
                          message: "La valeur minimum est 1",
                        },
                        valueAsNumber: true,
                      })}
                      isInvalid={
                        !!errors.permissions?.[permission.role]?.minOrder
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.permissions?.[permission.role]?.minOrder?.message}
                    </Form.Control.Feedback>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Illimité si vide ou 0"
                      {...register(
                        `permissions.${permission.role}.maxOrder` as any,
                        {
                          min: {
                            value: 0,
                            message: "La valeur minimum est 0",
                          },
                          valueAsNumber: true,
                        }
                      )}
                      isInvalid={
                        !!errors.permissions?.[permission.role]?.maxOrder
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.permissions?.[permission.role]?.maxOrder?.message}
                    </Form.Control.Feedback>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Modal>
  );
};

export default forwardRef(ManageCompanyStatusPermissionsModal);
